const aiCapabilities = [
  {
    title: "무음 구간 제거",
    desc: "불필요한 침묵과 의도적인 포즈를 구별합니다. 코믹 타이밍, 극적 긴장감, 자연스러운 호흡을 보존하는 맥락 인식 제거 기능을 제공합니다.",
    icon: "ri-scissors-cut-line",
    tag: "스마트 컷",
    tagColor: "#00D9FF",
  },
  {
    title: "다이나믹 컷 생성",
    desc: "단순 무음 기준이 아닌 내러티브 흐름 분석을 기반으로 편집 포인트를 생성합니다. AI가 스토리 아크, 페이싱, 시청자 집중 곡선을 이해합니다.",
    icon: "ri-movie-line",
    tag: "내러티브 AI",
    tagColor: "#10B981",
  },
  {
    title: "맥락 보존",
    desc: "컷 전반에 걸쳐 대화 일관성과 스토리 맥락을 유지합니다. 펀치라인 전 설정이나 반전 전 맥락을 절대 제거하지 않습니다.",
    icon: "ri-brain-line",
    tag: "딥 컨텍스트",
    tagColor: "#F59E0B",
  },
  {
    title: "페이싱 최적화",
    desc: "콘텐츠 유형에 맞게 리듬과 템포를 자동 조정합니다. 게임 하이라이트는 빠른 컷, 인터뷰는 안정적인 페이싱으로 처리합니다.",
    icon: "ri-pulse-line",
    tag: "자동 페이싱",
    tagColor: "#00D9FF",
  },
];

const editingSteps = [
  { step: "01", label: "방송 파일 수신", icon: "ri-upload-cloud-line", status: "complete" },
  { step: "02", label: "콘텐츠 구조 분석", icon: "ri-bar-chart-grouped-line", status: "complete" },
  { step: "03", label: "핵심 구간 감지", icon: "ri-scan-line", status: "complete" },
  { step: "04", label: "AI 편집 엔진", icon: "ri-robot-line", status: "active" },
  { step: "05", label: "품질 검토", icon: "ri-shield-check-line", status: "pending" },
  { step: "06", label: "숏폼 배포", icon: "ri-send-plane-line", status: "pending" },
];

export default function AIEditingSection() {
  return (
    <section className="py-28 bg-[#0A0E14] relative overflow-hidden">
      <div className="absolute top-1/2 left-0 w-96 h-96 rounded-full bg-[#00D9FF]/4 blur-3xl pointer-events-none -translate-y-1/2" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#00D9FF] text-xs font-semibold uppercase tracking-widest mb-4 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            03 / AI 편집 시스템
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            절대 <span className="text-[#00D9FF]">쉬지 않는</span> 편집자
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            AI 편집 엔진은 페이싱, 맥락, 스토리텔링을 이해합니다. 단순히 자르는 것이 아니라 완성된 작품을 만들어냅니다.
            인간의 감각을 잃지 않으면서 기계의 속도로 작동합니다.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-1 mb-16">
          {editingSteps.map((s, i) => (
            <div key={s.step} className="flex items-center">
              <div
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-medium transition-all ${
                  s.status === "complete"
                    ? "bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30"
                    : s.status === "active"
                    ? "bg-[#00D9FF]/15 text-[#00D9FF] border border-[#00D9FF]/40"
                    : "bg-[#1A1F2E] text-gray-500 border border-white/8"
                }`}
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  {s.status === "complete" ? (
                    <i className="ri-checkbox-circle-fill text-sm" />
                  ) : s.status === "active" ? (
                    <span className="w-2 h-2 rounded-full bg-[#00D9FF] animate-pulse inline-block" />
                  ) : (
                    <i className={`${s.icon} text-sm`} />
                  )}
                </span>
                {s.label}
              </div>
              {i < editingSteps.length - 1 && (
                <i className="ri-arrow-right-s-line text-gray-700 mx-1" />
              )}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="bg-[#0F1419] border border-white/8 rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/8">
              <span className="text-white text-sm font-semibold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>AI 편집기 – 라이브 세션</span>
              <span className="flex items-center gap-1.5 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#00D9FF" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#00D9FF] animate-pulse inline-block" />
                처리 중
              </span>
            </div>
            <div className="p-6">
              <div className="text-xs text-gray-500 mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                원본 방송 에피소드 — 총 62:18
              </div>
              <div className="h-10 bg-[#1A1F2E] rounded-lg overflow-hidden flex mb-2 relative">
                {[
                  { width: "8%", color: "#EF4444", opacity: 0.6 },
                  { width: "14%", color: "#10B981", opacity: 1 },
                  { width: "6%", color: "#EF4444", opacity: 0.4 },
                  { width: "18%", color: "#00D9FF", opacity: 1 },
                  { width: "5%", color: "#EF4444", opacity: 0.5 },
                  { width: "12%", color: "#10B981", opacity: 0.8 },
                  { width: "9%", color: "#EF4444", opacity: 0.3 },
                  { width: "16%", color: "#00D9FF", opacity: 1 },
                  { width: "12%", color: "#F59E0B", opacity: 0.7 },
                ].map((seg, i) => (
                  <div key={i} className="h-full" style={{ width: seg.width, backgroundColor: seg.color, opacity: seg.opacity }} />
                ))}
              </div>
              <div className="text-xs text-gray-600 flex justify-between mb-5">
                <span>0:00</span><span>31:09</span><span>62:18</span>
              </div>
              <div className="text-xs text-gray-500 mb-3" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                AI 편집 결과 — 3:42 선택
              </div>
              <div className="h-10 bg-[#1A1F2E] rounded-lg overflow-hidden flex mb-2">
                {[
                  { width: "28%", color: "#10B981" },
                  { width: "4%", color: "transparent" },
                  { width: "38%", color: "#00D9FF" },
                  { width: "4%", color: "transparent" },
                  { width: "26%", color: "#F59E0B" },
                ].map((seg, i) => (
                  <div key={i} className="h-full" style={{ width: seg.width, backgroundColor: seg.color, opacity: 0.9 }} />
                ))}
              </div>
              <div className="mt-6 grid grid-cols-3 gap-4 pt-5 border-t border-white/8">
                {[
                  { label: "원본", value: "62:18", icon: "ri-film-line", color: "#6B7280" },
                  { label: "AI 결과물", value: "3:42", icon: "ri-scissors-cut-line", color: "#00D9FF" },
                  { label: "절약 시간", value: "94.1%", icon: "ri-time-line", color: "#10B981" },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2" style={{ backgroundColor: `${s.color}15` }}>
                      <i className={`${s.icon} text-sm`} style={{ color: s.color }} />
                    </div>
                    <div className="font-bold text-lg mb-0.5" style={{ color: s.color, fontFamily: "'JetBrains Mono', monospace" }}>{s.value}</div>
                    <div className="text-gray-600 text-xs">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            {aiCapabilities.map((cap) => (
              <div key={cap.title} className="bg-[#0F1419] border border-white/8 rounded-xl p-5 hover:border-[#00D9FF]/20 transition-all duration-300 group cursor-pointer">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: `${cap.tagColor}12` }}>
                    <i className={`${cap.icon} text-lg`} style={{ color: cap.tagColor }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-white font-semibold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{cap.title}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ color: cap.tagColor, backgroundColor: `${cap.tagColor}15`, fontFamily: "'JetBrains Mono', monospace" }}>{cap.tag}</span>
                    </div>
                    <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{cap.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
